import { FC, useCallback, useEffect, useMemo, useState } from "react";
import type { TableHeading } from "types/Table";
import { ChildrenTable, SortOrder } from "types/Table";
import { useLazyQuery, useReactiveVar } from "@apollo/client";
import { CLIENT_QUOTE_HISTORY } from "../../Services/Queries/Queries";
import { Params } from "../../../../types/Main";
import { setNotification, userContextData } from "../../../../helpers/cache";
import { onChangeParamsCallback } from "../../../../helpers/Main";
import { DataCollectionItem } from "../../../../types/AdminDashboard";
import { Link } from "react-router-dom";
import { convertDateToUSFormat } from "../../../../helpers/utils";
import TableWithHeaderNew from "../../../../components/Table/TableWithHeaderNew";
import { QuoteStatusList } from "types/Quote";
import { QUOTE_STATUSES_QUERY } from "modules/Services/Queries/Common/CommonQueries";
import { QuoteStatus } from "types/QuoteStatus";

const EditClientHistoryNew: FC<{
  clientId?: number;
}> = ({ clientId }) => {
  const [fetchClientHistory] = useLazyQuery(CLIENT_QUOTE_HISTORY, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
  });

  const [isNeedUpdateData, setIsNeedUpdateData] = useState(false);
  const [quoteStatuses, setQuoteStatuses] = useState<QuoteStatus[]>([]);

  const tableHeading = useMemo(
    (): TableHeading => [
      {
        name: "id",
        label: "Id",
        sortable: true,
      },
      {
        name: "name",
        label: "Name",
        sortable: true,
      },
      {
        name: "company",
        label: "Company",
        sortable: true,
      },
      {
        name: "phone1",
        label: "Phone_1",
        sortable: true,
      },
      {
        name: "status",
        label: "Order Status",
        sortable: true,
      },
      {
        name: "dateCreated",
        label: "Date_created",
        sortable: true,
      },
    ],
    [],
  );
  const userDetails: any = useReactiveVar(userContextData);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<SortOrder>({
    columnName: "dateCreated",
    orderBy: "DESC",
  });
  const [dataCollection, setDataCollection] = useState<DataCollectionItem[]>(
    [],
  );
  const [countPages, setCountPages] = useState(0);
  const [prevFetchSettings, setPrevFetchSettings] = useState<Params>({});

  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [getQuoteStatuses] = useLazyQuery(QUOTE_STATUSES_QUERY, {
    notifyOnNetworkStatusChange: true,
  });

  const getQuoteStatusesHandle = useCallback(async () => {
    await getQuoteStatuses().then((res) => {
      if (res?.data?.configQuotesStatuses)
        setQuoteStatuses(res.data.configQuotesStatuses);
    });
  }, []);

  useEffect(() => {
    const countItemPerPage = parseInt(
      userDetails?.user?.userSettings?.filter(
        (count: any) => count.name === "clients_default_count",
      )?.[0]?.value,
    );

    setPageSize(countItemPerPage || 10);
  }, [userDetails]);

  useEffect(() => {
    const currentParams = {
      pageSize,
      currentPage,
      clientId,
      sortOrder,
      isNeedUpdateData,
    };

    onChangeParamsCallback(
      prevFetchSettings,
      currentParams,
      (newParams) => setPrevFetchSettings(newParams),
      () => {
        setIsNeedUpdateData(true);
      },
    );
  }, [pageSize, currentPage, clientId, sortOrder, prevFetchSettings]);

  useEffect(() => {
    if (isNeedUpdateData) {
      getQuoteStatusesHandle();
      onFetchHistory();
    }
  }, [isNeedUpdateData]);

  const onErrorFetch = useCallback(() => {
    setCountPages(0);
    setDataCollection([]);
  }, []);

  const onFetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    await fetchClientHistory({
      variables: {
        clientId,
        limit: pageSize,
        page: currentPage,
        sortBy: {
          column: sortOrder.columnName,
          order: sortOrder.orderBy,
        },
      },
    })
      .then((res) => {
        const { data } = res;

        if (!data) {
          onErrorFetch();
          return;
        }

        const { clientsQuoteHistory } = data;

        if (!clientsQuoteHistory) {
          onErrorFetch();
          return;
        }

        const { dataCollection, totalNumberOfItems } = clientsQuoteHistory;

        if (!dataCollection || !totalNumberOfItems) {
          onErrorFetch();
          return;
        }

        const newCountPages = Math.ceil(totalNumberOfItems / pageSize);

        setDataCollection(dataCollection);
        setCountPages(newCountPages);
      })
      .finally(() => {
        setIsLoadingHistory(false);

        if (isNeedUpdateData) {
          setIsNeedUpdateData(false);
        }
      });
  }, [isNeedUpdateData, pageSize, clientId, currentPage, sortOrder]);

  const childrenForTable = useMemo((): ChildrenTable[] => {
    let currentChildrenForTable: ChildrenTable[] = [];

    dataCollection.forEach((dataCollectionItem) => {
      const { id, name, company, phone1, createdAt, status } =
        dataCollectionItem;

      const getStatusName = () => {
        console.log(quoteStatuses, status);
        const currentStatus = quoteStatuses?.find(
          (currentStatus) => currentStatus.id == status,
        );

        return currentStatus?.name || "-";
      };

      currentChildrenForTable.push({
        id: id,
        row: {
          id: <Link to={`/quotes/${id}/quote_info`}>{id}</Link>,
          name: name || "-",
          company: company || "-",
          phone1: phone1 || "-",
          status: getStatusName(),
          dateCreated: createdAt ? convertDateToUSFormat(createdAt) : "-",
        },
      });
    });

    return currentChildrenForTable;
  }, [dataCollection]);
  return (
    <section>
      <TableWithHeaderNew
        header="Quote history"
        heading={tableHeading}
        currentItemsPerPage={pageSize}
        currentPage={currentPage}
        countPages={countPages}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        loading={isLoadingHistory}
        children={childrenForTable}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        setIsNeedUpdateTableData={() => {
          setIsNeedUpdateData(true);
        }}
      />
    </section>
  );
};

export default EditClientHistoryNew;
